/**
 * SuperAdmin Forms checklist -> "New patient & medical history messages" row + "Load default form messages" preview (Forms Settings,
 * WP3, 2026-10-20). Plan: docs/plans/2026-10-20-forms-settings-plan.md section 2.7, 4 WP3.
 *
 * teamio-admin `SEED_FORM_MESSAGES` (seed-form-messages.js) writes the two message blobs - NEW_PATIENT_FORMS_SETTINGS and
 * FORMS_UPDATE_REMINDER_SETTINGS - each WHOLE-BLOB set-if-absent, switch OFF, with the form link pre-picked when exactly one candidate
 * exists. `GET_SITE_FEATURE_STATUS` reports `readiness.forms.formMessages` (below). This file only READS those two answers and words
 * them for the operator; it decides nothing the backend decides.
 *
 *   readiness.forms.formMessages = {
 *     newPatient: { present, corrupt, linksPicked, linksNeeded, enabled },
 *     medHx:      { present, corrupt, inline, linksPicked, linksNeeded, activated },
 *   }
 *   SEED_FORM_MESSAGES response = { siteName, dryRun, warnings,
 *     newPatient: { alreadySet, corrupt, willWrite, written, links:{sms,email}, linkReason },
 *     medHx:      { alreadySet, corrupt, willWrite, written, links:{sms,email}, linkReason, triggerForms:[{FormIDForWeb, Name}] } }
 *
 * Import-free: cypress/node-tests/superadmin-form-messages-seed.test.js executes this file verbatim.
 */

export const FORM_MESSAGES_ACTION = 'SEED_FORM_MESSAGES';

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const count = (v) => (typeof v === 'number' && isFinite(v) && v > 0 ? v : 0);

/** The reported readiness, or null when an older teamio-admin does not report it yet (the row then says "readiness unavailable"). */
export function formMessagesReadinessOf(formsReadiness) {
  const fm = isObj(formsReadiness) ? formsReadiness.formMessages : null;
  return isObj(fm) && isObj(fm.newPatient) && isObj(fm.medHx) ? fm : null;
}

// One blob -> { linked, line }. linked = nothing is left for the office to pick.
function blobStatus(name, b, isMedHx) {
  if (!b.present) return { linked: false, line: `${name}: not loaded yet` };
  if (b.corrupt) return { linked: false, line: `${name}: the saved settings are unreadable — the office re-saves them in Forms → Settings` };
  const needed = count(b.linksNeeded);
  const picked = Math.min(count(b.linksPicked), needed);
  // an office's own wiki template carries its link inside the page: nothing to pick here
  if (isMedHx && !b.inline) return { linked: true, line: `${name}: uses the office's own templates (no link to pick)` };
  if (needed === 0) return { linked: true, line: `${name}: no message is turned on` };
  if (picked >= needed) return { linked: true, line: `${name}: ${picked}/${needed} links picked` };
  if (picked === 0) return { linked: false, line: `${name}: no link picked — the office picks it in Forms → Settings` };
  return { linked: false, line: `${name}: ${needed - picked} of ${needed} messages have no link — the office picks it in Forms → Settings` };
}

function switchText(np, mh) {
  const a = !!np.enabled;
  const b = !!mh.activated;
  if (a && b) return 'both ON';
  if (a) return 'New Patients ON';
  if (b) return 'Medical History ON';
  return 'both OFF';
}

/**
 * The checklist row's facts, or null when the readiness is unavailable.
 * ok = both blobs are saved, readable, and every message that is on has its link. badge e.g. "2/2 links picked · both OFF".
 */
export function summarizeFormMessages(fm) {
  if (!isObj(fm) || !isObj(fm.newPatient) || !isObj(fm.medHx)) return null;
  const np = blobStatus('New Patients', fm.newPatient, false);
  const mh = blobStatus('Medical History', fm.medHx, true);
  const loaded = !!(fm.newPatient.present || fm.medHx.present);
  const linked = (np.linked ? 1 : 0) + (mh.linked ? 1 : 0);
  return {
    ok: linked === 2,
    linked,
    badge: loaded ? `${linked}/2 links picked · ${switchText(fm.newPatient, fm.medHx)}` : 'not loaded yet',
    detail: `${np.line} · ${mh.line}`,
  };
}

// ---- the dry-run preview -------------------------------------------------------------------------------------------------------

function linkText(link) {
  if (!isObj(link) || typeof link.id !== 'string' || link.id === '') return 'no link picked';
  const kind = link.kind === 'packet' ? 'packet' : 'form';
  return `opens ${typeof link.name === 'string' && link.name !== '' ? link.name : link.id} (${kind})`;
}

function previewCard(key, title, blob) {
  const status = blob.alreadySet ? (blob.corrupt ? 'corrupt' : 'set') : 'write';
  const card = {
    key,
    title,
    status,
    statusText: status === 'write' ? 'Will be written'
      : (status === 'corrupt' ? 'Saved but unreadable — left exactly as it is' : 'Already saved — left exactly as it is'),
    messages: [],
    reason: null,
    triggerForms: [],
  };
  if (status !== 'write') return card;
  const links = isObj(blob.links) ? blob.links : {};
  card.messages = [
    { label: 'Text message', linkText: linkText(links.sms) },
    { label: 'Email', linkText: linkText(links.email) },
  ];
  const unpicked = !isObj(links.sms) || !isObj(links.email);
  if (unpicked) {
    const why = typeof blob.linkReason === 'string' && blob.linkReason !== '' ? `: ${blob.linkReason}` : '';
    card.reason = `No link picked${why}. The office picks it in Forms → Settings.`;
  }
  card.triggerForms = (Array.isArray(blob.triggerForms) ? blob.triggerForms : [])
    .map((f) => (isObj(f) && typeof f.Name === 'string' && f.Name !== '' ? f.Name : (isObj(f) ? f.FormIDForWeb : null)))
    .filter((n) => typeof n === 'string' && n !== '');
  return card;
}

/** A SEED_FORM_MESSAGES dry-run answer -> the preview the modal draws, or null when the answer is not that shape. */
export function describeFormMessagesPreview(data) {
  if (!isObj(data) || !isObj(data.newPatient) || !isObj(data.medHx)) return null;
  const cards = [
    previewCard('newPatient', 'New Patients', data.newPatient),
    previewCard('medHx', 'Medical History Update', data.medHx),
  ];
  const willWriteCount = cards.filter((c) => c.status === 'write').length;
  return {
    siteName: typeof data.siteName === 'string' ? data.siteName : '',
    cards,
    warnings: Array.isArray(data.warnings) ? data.warnings.filter((w) => typeof w === 'string' && w !== '') : [],
    willWriteCount,
    nothingToWrite: willWriteCount === 0,
  };
}

/** A real-run answer -> one sentence for the toast. Both switches are always left OFF. */
export function describeFormMessagesResult(data) {
  if (!isObj(data) || !isObj(data.newPatient) || !isObj(data.medHx)) return 'Form messages loaded.';
  const wrote = [];
  const kept = [];
  [['New Patients', data.newPatient], ['Medical History', data.medHx]].forEach(([name, b]) => (b.written ? wrote : kept).push(name));
  if (wrote.length === 0) return 'Nothing was written — both message settings were already saved on this site (left as they are).';
  const tail = kept.length ? ` ${kept.join(' and ')} ${kept.length === 1 ? 'was' : 'were'} already saved (left as is).` : '';
  return `Loaded the ${wrote.join(' and ')} messages — left OFF for the office to turn on in Forms → Settings.${tail}`;
}
